<template>
    <v-card :loading="loading">
        <v-card-title>
            <v-btn small icon @click="close" color="primary" class="mr-2">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <h3>Mark as Sent to Site</h3>
        </v-card-title>
        <v-divider class="ml-4 mr-5" />
        <v-card-text class="pt-4">
            <v-row>
                <v-col cols="12" md="6">
                    <v-autocomplete
                        v-model="selectedProject"
                        :items="projects"
                        item-text="label"
                        item-value="id"
                        return-object
                        label="Project"
                        outlined
                        dense
                        :loading="loadingProjects"
                        @change="onProjectChange"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <v-autocomplete
                        v-model="selectedScope"
                        :items="scopeOptions"
                        item-text="text"
                        item-value="value"
                        return-object
                        label="Scope"
                        outlined
                        dense
                        :disabled="!selectedProject"
                        :loading="loadingScopes"
                        no-data-text="Select a project first"
                    />
                </v-col>
                <v-col cols="12" md="4">
                    <v-menu
                        v-model="deliveryDateMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="deliveryDate"
                                label="Delivery Date"
                                outlined
                                dense
                                readonly
                                prepend-inner-icon="mdi-calendar"
                                v-bind="attrs"
                                v-on="on"
                            />
                        </template>
                        <v-date-picker
                            v-model="deliveryDate"
                            @input="deliveryDateMenu = false"
                        />
                    </v-menu>
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field
                        v-model="contactPerson"
                        label="Contact Person"
                        outlined
                        dense
                    />
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field v-model="toField" label="To" outlined dense />
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field v-model="note" label="Note" outlined dense />
                </v-col>
                <v-col cols="12">
                    <v-autocomplete
                        v-model="selectedRowKeys"
                        :items="selectableItems"
                        item-text="label"
                        item-value="rowKey"
                        label="Items"
                        outlined
                        dense
                        multiple
                        chips
                        deletable-chips
                        small-chips
                    />
                </v-col>
            </v-row>

            <v-data-table
                v-if="formItems.length > 0"
                :headers="itemHeaders"
                :items="formItems"
                class="elevation-0 mt-2"
                disable-pagination
                hide-default-footer
                :mobile-breakpoint="0"
            >
                <template v-slot:[`item.qtyToSend`]="{ item: row }">
                    <v-text-field
                        v-model.number="row.qtyToSend"
                        type="number"
                        min="1"
                        :max="row.remainingQty"
                        dense
                        outlined
                        :error-messages="qtyError(row)"
                        :hint="`Max: ${row.remainingQty}`"
                        persistent-hint
                        class="my-1"
                    />
                </template>
            </v-data-table>
        </v-card-text>
        <v-card-actions>
            <v-spacer />
            <v-btn
                rounded
                color="primary"
                class="mx-2 mb-3"
                :disabled="!canSend"
                :loading="loading"
                @click="send"
            >
                Sent
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import API from '@/services/api'
import { mapMutations } from 'vuex'
import { storage } from '@/services/firebase'
import moment from 'moment'
import { generateDeliveryTicketPDF } from '@/components/StockRoom/deliveryTicketPDF'

export default {
    name: 'MarkSentToSite',
    props: {
        item: {
            type: Object,
            required: true,
        },
        availableItems: {
            type: Array,
            default: () => [],
        },
        companyId: {
            type: String,
            required: true,
        },
        projects: {
            type: Array,
            default: () => [],
        },
        loadingProjects: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        loading: false,
        loadingScopes: false,
        scopeOptions: [],
        users: [],
        logoBase64: '',
        selectedProject: null,
        selectedScope: null,
        deliveryDate: '',
        deliveryDateMenu: false,
        contactPerson: '',
        note: '',
        toField: '',
        selectedRowKeys: [],
        formItems: [],
        itemHeaders: [
            {
                text: 'ITEM CODE',
                value: 'itemCode',
                align: 'center',
                sortable: false,
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                align: 'left',
                sortable: false,
            },
            {
                text: 'WAREHOUSE RECEIVED',
                value: 'warehouseReceived',
                align: 'center',
                sortable: false,
            },
            {
                text: 'ALREADY SENT',
                value: 'alreadySent',
                align: 'center',
                sortable: false,
            },
            {
                text: 'QTY TO SEND',
                value: 'qtyToSend',
                align: 'center',
                sortable: false,
            },
        ],
    }),
    computed: {
        selectableItems() {
            return this.availableItems.map(row => ({
                ...row,
                label: `${row.project || '-'} | ${row.itemCode} | ${
                    row.packingListCode
                } | ${row.packageLabel}`,
            }))
        },
        canSend() {
            if (
                !this.selectedProject ||
                !this.selectedScope ||
                !this.deliveryDate ||
                this.formItems.length === 0
            ) {
                return false
            }
            return this.formItems.every(row => {
                const qty = Number(row.qtyToSend)
                return qty > 0 && qty <= Number(row.remainingQty || 0)
            })
        },
    },
    watch: {
        selectedRowKeys: {
            handler(keys) {
                const keySet = new Set(keys || [])
                const existing = {}
                this.formItems.forEach(row => {
                    existing[row.rowKey] = row.qtyToSend
                })
                this.formItems = this.selectableItems
                    .filter(row => keySet.has(row.rowKey))
                    .map(row => ({
                        ...row,
                        qtyToSend:
                            existing[row.rowKey] !== undefined &&
                            existing[row.rowKey] !== ''
                                ? existing[row.rowKey]
                                : row.remainingQty,
                    }))
            },
            immediate: false,
        },
    },
    async created() {
        const triggerKey = this.getRowKey(this.item)
        this.selectedRowKeys = [triggerKey]
        this.syncFormItems()
        await this.loadInitialData()
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        getRowKey(row) {
            return `${row.packingListId}_${row.packageId}_${row.itemIndex}`
        },
        syncFormItems() {
            const keySet = new Set(this.selectedRowKeys || [])
            this.formItems = this.selectableItems
                .filter(row => keySet.has(row.rowKey))
                .map(row => ({
                    ...row,
                    qtyToSend: row.remainingQty,
                }))
        },
        qtyError(row) {
            const qty = Number(row.qtyToSend)
            const maxQty = Number(row.remainingQty || 0)
            if (
                row.qtyToSend === '' ||
                row.qtyToSend === null ||
                row.qtyToSend === undefined
            ) {
                return ''
            }
            if (qty <= 0) {
                return 'Qty must be greater than zero'
            }
            if (qty > maxQty) {
                return `Cannot exceed remaining qty (${maxQty})`
            }
            return ''
        },
        async loadInitialData() {
            try {
                const {
                    data: { settings },
                } = await API.getSettings()
                const companySetting = settings.find(s => s.name === 'Company')
                this.logoBase64 = companySetting?.generalInfo?.logoBase64 || ''
                const {
                    data: { users },
                } = await API.getLiteUsers()
                this.users = users || []
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        async onProjectChange() {
            this.selectedScope = null
            this.scopeOptions = []
            if (!this.selectedProject?.id) {
                return
            }
            try {
                this.loadingScopes = true
                const quotes = await API.getProjectQuotes(
                    this.selectedProject.id
                )
                const options = []
                for (const quote of quotes || []) {
                    const costs = await API.getCosts(quote.id)
                    ;(costs || [])
                        .filter(cost => cost.type === 'cost')
                        .forEach(cost => {
                            options.push({
                                text: `(${quote.number}) ${quote.name} / ${cost.reference}`,
                                value: {
                                    quoteId: quote.id,
                                    scopeId: cost.id,
                                    scopeReference: cost.reference,
                                    quoteName: quote.name,
                                    quoteNumber: quote.number,
                                },
                            })
                        })
                }
                this.scopeOptions = options
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loadingScopes = false
            }
        },
        resolveProjectManagerName(project) {
            if (!project?.projectManager) {
                return ''
            }
            const user = this.users.find(u => u.id === project.projectManager)
            return user?.name || ''
        },
        generateTicketId() {
            return String(Math.floor(100000 + Math.random() * 900000))
        },
        close() {
            this.$emit('closeDialog')
        },
        async send() {
            try {
                this.loading = true
                const ticketId = this.generateTicketId()
                const nameFile = `deliveryTicket_${ticketId}.pdf`
                const projectLabel = this.selectedProject.label
                const scopeLabel = this.selectedScope.value.scopeReference
                const projectManager = this.resolveProjectManagerName(
                    this.selectedProject
                )
                const deliveryDateLabel = moment(this.deliveryDate).format(
                    'MM/DD/YYYY'
                )

                const pdfItems = this.formItems.map(row => ({
                    qty: Number(row.qtyToSend),
                    itemCode: row.itemCode,
                    description: row.description,
                    finishAndUCCode: row.finishAndUCCode,
                }))

                const pdfBlob = await generateDeliveryTicketPDF({
                    logoBase64: this.logoBase64,
                    projectLabel,
                    scopeLabel,
                    deliveryDate: deliveryDateLabel,
                    contactPerson: this.contactPerson,
                    note: this.note,
                    to: this.toField,
                    projectManager,
                    items: pdfItems,
                })

                const payload = {
                    viewerProjectId: this.selectedProject.id,
                    quoteId: this.selectedScope.value.quoteId,
                    scopeId: this.selectedScope.value.scopeId,
                    deliveryDate: this.deliveryDate,
                    contactPerson: this.contactPerson,
                    note: this.note,
                    to: this.toField,
                    nameFile,
                    items: this.formItems.map(row => ({
                        projectId: row.projectId,
                        packingListId: row.packingListId,
                        packageId: row.packageId,
                        itemIndex: row.itemIndex,
                        qty: Number(row.qtyToSend),
                    })),
                }

                const pdfFile = new File([pdfBlob], nameFile, {
                    type: 'application/pdf',
                })
                const storagePath = `${this.companyId}/deliveryTickets`
                const storageRef = storage().ref(storagePath)
                await storageRef.child(nameFile).put(pdfFile, {
                    customMetadata: { filename: nameFile },
                })

                const result = await API.sendPackingItemsToSite(payload)
                this.$emit('saved', {
                    ...payload,
                    packingLists: result?.packingLists || [],
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>
